import { reactive } from 'vue';

/**
 * The app's toast system (GaiaToastHost renders it, top-right).
 *
 * Deliberately NOT naive's useMessage: naive's MessageProvider has no render
 * hook and its API is inject-only, which forced ~57 lines of global CSS
 * surgery in App.vue and made toasts unusable from stores. This state lives at
 * module scope instead — `useGaiaToast()` works anywhere (setup, composable
 * factory, store, plain function), and a toast fired before the host mounts
 * simply waits in the queue: timers are owned by the host, so nothing can
 * expire unseen.
 */

export type GaiaToastType = 'success' | 'error' | 'warning' | 'info' | 'loading';

export interface GaiaToastHandle {
  destroy: () => void;
}

/** A follow-up the toast offers ("View in Upload History"); clicking it also dismisses. */
export interface GaiaToastAction {
  label: string;
  onClick: () => void;
}

export interface GaiaToastOptions {
  /** Milliseconds on screen; 0 keeps the toast until `destroy()`. */
  duration?: number;
  action?: GaiaToastAction;
}

export interface GaiaToastItem {
  id: number;
  type: GaiaToastType;
  content: string;
  duration: number;
  action?: GaiaToastAction;
}

export interface GaiaToastApi {
  success: (content: string, options?: GaiaToastOptions) => GaiaToastHandle;
  error: (content: string, options?: GaiaToastOptions) => GaiaToastHandle;
  warning: (content: string, options?: GaiaToastOptions) => GaiaToastHandle;
  info: (content: string, options?: GaiaToastOptions) => GaiaToastHandle;
  loading: (content: string, options?: GaiaToastOptions) => GaiaToastHandle;
}

/**
 * How long each tone stays. An error is something the user has to read and
 * often act on, so it lingers; loading is sticky by definition — its caller
 * holds the handle and destroys it when the work settles.
 */
const DEFAULT_DURATION: Record<GaiaToastType, number> = {
  success: 3500,
  info: 3500,
  warning: 5000,
  error: 7000,
  loading: 0,
};

/** A toast with an action stays long enough to reach for the button. */
const ACTION_MIN_DURATION = 7000;

/** A runaway error loop must not wallpaper the screen. */
const MAX_TOASTS = 8;

/** The live queue — consumed only by GaiaToastHost. */
export const gaiaToasts = reactive<GaiaToastItem[]>([]);

export function removeGaiaToast(id: number): void {
  const index = gaiaToasts.findIndex(toast => toast.id === id);
  if (index !== -1) gaiaToasts.splice(index, 1);
}

let nextId = 1;

function push(type: GaiaToastType, content: string, options?: GaiaToastOptions): GaiaToastHandle {
  const id = nextId++;
  const action = options?.action;
  const fallback = action
    ? Math.max(DEFAULT_DURATION[type], ACTION_MIN_DURATION)
    : DEFAULT_DURATION[type];
  gaiaToasts.push({ id, type, content, duration: options?.duration ?? fallback, action });

  // Evict the oldest dismissible toast past the cap. Loading toasts are
  // exempt: their callers hold handles and expect them to survive.
  if (gaiaToasts.length > MAX_TOASTS) {
    const oldest = gaiaToasts.find(toast => toast.type !== 'loading');
    if (oldest) removeGaiaToast(oldest.id);
  }

  return {
    destroy: (): void => {
      removeGaiaToast(id);
    },
  };
}

const api: GaiaToastApi = {
  success: (content, options) => push('success', content, options),
  error: (content, options) => push('error', content, options),
  warning: (content, options) => push('warning', content, options),
  info: (content, options) => push('info', content, options),
  loading: (content, options) => push('loading', content, options),
};

/**
 * Drop-in replacement for naive's `useGaiaToast()` — same call shape
 * (`toast.success('…')`, `toast.loading('…', { duration: 0 })` → `.destroy()`)
 * but callable from anywhere, not just component setup.
 */
export function useGaiaToast(): GaiaToastApi {
  return api;
}
